import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SourcingService, VonqContracts } from 'api-clients/monolith';
import _range from 'lodash/range';
import { Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import EmptyChannels from '@/assets/icons/empty-channels.svg';
import { useConfirmation } from '@/components/base/confirmation';
import { useToaster } from '@/components/base/toaster';
import { TrashIcon } from '@/components/icons/trash';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { queryKeys } from '@/query-keys';
import { formatShortDate } from '@/utils/date-util';

import { ContractsTableHeader } from './table-header';

interface ContractsTableProps {
  contracts: VonqContracts['contracts'];
  disabled?: boolean;
}

export function ContractsTable({ contracts, disabled }: ContractsTableProps) {
  const gridTemplateColumns = '1fr 1fr';
  const intl = useIntl();
  const { confirm } = useConfirmation();
  const { notifySuccess, notifyError } = useToaster();
  const queryClient = useQueryClient();

  const { mutate: removeContract } = useMutation({
    mutationFn: (vonqContractId: string) =>
      SourcingService.deleteInternalApiSourcingVonqContractsReference(
        vonqContractId,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.settings.contracts.vonqContracts],
      });

      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Contract removed successfully',
          description: 'Success toast after removing a Vonq contract',
        }),
      });
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to remove contract',
          description: 'Error toast when Vonq contract removal API fails',
        }),
      });
    },
  });

  const handleRemoveContract = async (
    contractId: string,
    contractName: string,
  ) => {
    const confirmTitle = intl.formatMessage(
      {
        defaultMessage: 'Remove {contractName}',
        description:
          'Dialog title when removing a job channel contract by name',
      },
      { contractName },
    );

    const isRemoveConfirmed = await confirm({
      title: confirmTitle,
      description: (
        <FormattedMessage
          defaultMessage="This contract will no longer be available when posting jobs. You can add it again at any time."
          description="Remove contract confirmation: explains effect on job posting"
        />
      ),
      confirmLabel: intl.formatMessage({
        defaultMessage: 'Remove',
        description:
          'Primary button to confirm removing a job channel contract',
      }),
      confirmVariant: 'destructive',
    });

    if (isRemoveConfirmed) {
      removeContract(contractId);
    }
  };

  if (contracts.length === 0)
    return (
      <EmptyState
        className="w-full pb-4"
        icon={EmptyChannels}
        header={intl.formatMessage({
          defaultMessage: 'No contracts configured yet!',
          description: 'Empty state for contracts list',
        })}
        description={intl.formatMessage({
          defaultMessage:
            'Create your first contract to track performance when posting jobs.',
          description:
            'Empty state for contracts list: encourage adding a contract',
        })}
      />
    );

  return (
    <>
      {/* Table headers */}
      <ContractsTableHeader gridTemplateColumns={gridTemplateColumns} />

      {/* Table rows */}
      {contracts.map(contract => (
        <Fragment key={contract.external_id}>
          <div
            className="grid items-center gap-x-4 px-4 py-3"
            style={{ gridTemplateColumns }}
          >
            {/* Contract details */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <ChannelIcon
                  channelSlug={contract.slug}
                  channelName={contract.name}
                  displayTitle={contract.name}
                  logoSquareUrl={contract.logo_square_url}
                  logoRectangleUrl={contract.logo_rectangle_url}
                  variant="large"
                  titleClassName="text-fx-neutral-600 font-normal"
                />
              </div>
              <Typography variant="small1" className="!text-fx-neutral-400">
                <FormattedMessage
                  defaultMessage="Added by {createdBy} · {createdAt}"
                  description="Metadata showing who added the contract and the short date it was added on"
                  values={{
                    createdBy: contract.created_by,
                    createdAt: formatShortDate(contract.created_at),
                  }}
                />
              </Typography>
            </div>

            {/* Actions */}
            <div className="flex items-center">
              <Button
                variant="ghost"
                type="button"
                disabled={disabled}
                onClick={() => {
                  void handleRemoveContract(
                    contract.external_id,
                    contract.name,
                  );
                }}
              >
                <TrashIcon className="text-fx-rose" />
                <FormattedMessage
                  defaultMessage="Remove Contract"
                  description="Label for the action button that deletes/removes an integrated contract from the list"
                />
              </Button>
            </div>
          </div>

          {/* Divider */}
          <div className="bg-fx-neutral-200 h-px w-full" />
        </Fragment>
      ))}
    </>
  );
}

ContractsTable.Skeleton = function ContractsTableSkeleton() {
  return (
    <>
      <ContractsTableHeader gridTemplateColumns="1fr 1fr" />

      {_range(3).map(index => (
        <Fragment key={index}>
          <div
            className="grid items-center gap-x-4 px-4 py-3"
            style={{ gridTemplateColumns: '1fr 1fr' }}
          >
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-5 rounded-full" />
                <Skeleton className="h-5 w-24" />
              </div>
              <Skeleton className="h-4 w-40" />
            </div>

            <div className="flex items-center gap-1">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 w-28" />
            </div>
          </div>

          <div className="bg-fx-neutral-200 h-px w-full" />
        </Fragment>
      ))}
    </>
  );
};
